import { useLayoutEffect } from 'react';
import { publicAbsoluteUrl } from '../utils/publicSite';

const ATTR = 'data-aacore-seo';

type SeoHeadProps = {
  title: string;
  description: string;
  canonicalPath: string;
  ogType?: 'website' | 'article';
  ogImage?: string | null;
  jsonLd?: object | object[] | null;
  robots?: string;
};

function upsertMeta(selector: string, attributes: Record<string, string>): HTMLMetaElement {
  let el = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    document.head.appendChild(el);
  }
  Object.entries(attributes).forEach(([key, value]) => el?.setAttribute(key, value));
  el.setAttribute(ATTR, 'true');
  return el;
}

function upsertLink(rel: string, href: string): HTMLLinkElement {
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  el.setAttribute(ATTR, 'true');
  return el;
}

const SeoHead: React.FC<SeoHeadProps> = ({
  title,
  description,
  canonicalPath,
  ogType = 'website',
  ogImage,
  jsonLd,
  robots,
}) => {
  const jsonLdKey = JSON.stringify(jsonLd ?? null);

  useLayoutEffect(() => {
    const canonical = publicAbsoluteUrl(canonicalPath);
    const previousTitle = document.title;
    document.title = title;

    const descriptionEl = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionEl?.getAttribute('content') || '';

    upsertMeta('meta[name="description"]', { name: 'description', content: description });
    upsertLink('canonical', canonical);
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: ogType });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonical });
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: 'AACore Booking' });
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: ogImage ? 'summary_large_image' : 'summary' });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });

    if (ogImage) {
      upsertMeta('meta[property="og:image"]', { property: 'og:image', content: ogImage });
      upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: ogImage });
    }

    const robotsSelector = `meta[name="robots"][${ATTR}="robots"]`;
    if (robots) {
      const robotsEl = upsertMeta('meta[name="robots"]', { name: 'robots', content: robots });
      robotsEl.setAttribute(ATTR, 'robots');
    } else {
      document.head.querySelector(robotsSelector)?.remove();
    }

    document.head.querySelectorAll('script[type="application/ld+json"]').forEach((node) => node.remove());
    const payloads = jsonLd == null ? [] : Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    payloads.forEach((payload) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute(ATTR, 'jsonld');
      script.text = JSON.stringify(payload);
      document.head.appendChild(script);
    });

    return () => {
      document.title = previousTitle;
      if (descriptionEl) descriptionEl.setAttribute('content', previousDescription);
      document.head.querySelectorAll(`script[${ATTR}="jsonld"]`).forEach((node) => node.remove());
      document.head.querySelector('meta[name="robots"][data-aacore-seo="robots"]')?.remove();
    };
  }, [title, description, canonicalPath, ogType, ogImage, jsonLd, jsonLdKey, robots]);

  return null;
};

export default SeoHead;
